export const MIN_CHARS = 2;
export const FUZZY_THRESHOLD = 0.78;
export const FUZZY_THRESHOLD_MEDIUM = 0.78;
export const FUZZY_THRESHOLD_LONG = 0.85;
export const SUGGESTION_DEBOUNCE_MS = 60;

export const normalizeTerm = (value) =>
  String(value || "").trim().toLowerCase();

export const getFuzzyThresholdForLength = (length) => {
  if (length <= 6) return FUZZY_THRESHOLD;
  if (length <= 9) return FUZZY_THRESHOLD_MEDIUM;
  return FUZZY_THRESHOLD_LONG;
};

export const levenshteinDistance = (a, b) => {
  const left = normalizeTerm(a);
  const right = normalizeTerm(b);
  if (!left && !right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;

  const matrix = Array.from({ length: left.length + 1 }, () =>
    Array(right.length + 1).fill(0),
  );

  for (let i = 0; i <= left.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= right.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[left.length][right.length];
};

export const isFuzzyMatchAccepted = (input, distance, score) => {
  const normalizedInput = normalizeTerm(input);
  const length = normalizedInput.length;
  if (!length) return false;

  // Short words: accept a single typo (caar -> car, caars -> cars).
  if (distance === 1 && length >= 4 && length <= 6) {
    return true;
  }

  return score >= getFuzzyThresholdForLength(length);
};

export const getFuzzyMatch = (input, keywords) => {
  const normalizedInput = normalizeTerm(input);
  if (!normalizedInput) return null;

  let bestMatch = null;
  let bestScore = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  (keywords || []).forEach((keyword) => {
    const normalizedKeyword = normalizeTerm(keyword);
    if (!normalizedKeyword || normalizedKeyword === normalizedInput) return;

    const distance = levenshteinDistance(normalizedInput, normalizedKeyword);
    const maxLen = Math.max(normalizedInput.length, normalizedKeyword.length);
    const score = maxLen === 0 ? 0 : 1 - distance / maxLen;

    if (
      score > bestScore ||
      (score === bestScore && distance < bestDistance)
    ) {
      bestScore = score;
      bestDistance = distance;
      bestMatch = keyword;
    }
  });

  if (!bestMatch) return null;

  return {
    term: bestMatch,
    score: bestScore,
    distance: bestDistance,
  };
};

export const getDidYouMean = (input, keywords) => {
  const normalizedInput = normalizeTerm(input);
  if (normalizedInput.length < MIN_CHARS) return null;

  const fuzzyMatch = getFuzzyMatch(normalizedInput, keywords);
  if (!fuzzyMatch) return null;

  if (
    !isFuzzyMatchAccepted(
      normalizedInput,
      fuzzyMatch.distance,
      fuzzyMatch.score,
    )
  ) {
    return null;
  }

  return fuzzyMatch.term;
};
